import React, { useEffect, useState, useCallback } from 'react'
import { supabase, AUTO_REFRESH_ENABLED } from '../supabaseClient'
import Calendar from './Calendar'
import Resources from './Resources'
import Attendance from './Attendance'
import Layout, { PageHeader, Grid4, MetricCard, Panel, TwoCol, Empty, ZoomBtn } from './Layout'
import ClassScheduleView from './ClassScheduleView'
import { isMeetLinkVisible } from '../meetHelper'
import ZoomRecordings from './ZoomRecordings'
import RescheduleManager from './RescheduleManager'
import SupportChat from './SupportChat'
import { checkoutUrl } from '../policies'

function useIsMobile() {
  const [mobile, setMobile] = useState(window.innerWidth < 768)
  useEffect(() => {
    const h = () => setMobile(window.innerWidth < 768)
    window.addEventListener('resize', h)
    return () => window.removeEventListener('resize', h)
  }, [])
  return mobile
}

export default function StudentDash({ profile }) {
  const [classes,       setClasses]       = useState([])
  const [enrollments,   setEnrollments]   = useState([])
  const [payments,      setPayments]      = useState([])
  const [courses,       setCourses]       = useState([])
  const [loading,       setLoading]       = useState(true)
  const [tab,           setTab]           = useState('overview')
  const [notifications, setNotifications] = useState([])
  const isMobile = useIsMobile()

  function markJoined(classId) {
    // Fire-and-forget — feeds the no-show check in api/send-class-reminders.js.
    supabase.from('enrollments').update({ joined_at: new Date().toISOString() })
      .eq('class_id', classId).eq('student_id', profile.id).then(() => {})
  }

  const load = useCallback(async () => {
    setLoading(true)
    const { data: enr } = await supabase.from('enrollments').select('*').eq('student_id', profile.id)
    const enrolledClassIds  = (enr||[]).map(e=>e.class_id).filter(Boolean)
    const enrolledCourseIds = (enr||[]).map(e=>e.course_id).filter(Boolean)

    let cls = []
    if (enrolledClassIds.length) {
      const { data } = await supabase.from('classes').select('*')
        .in('id', enrolledClassIds)
        .neq('is_cancelled', true)
        .order('class_date',{ascending:true})
      cls = data||[]
    }
    const { data: pay } = await supabase.from('payments').select('*').eq('student_id', profile.id).order('created_at',{ascending:false})
    let crss = []
    if (enrolledCourseIds.length) {
      const { data } = await supabase.from('courses').select('*').in('id', enrolledCourseIds)
      crss = data||[]
    }
    const { data: notifs } = await supabase.from('notifications').select('*').eq('user_id', profile.id).order('created_at',{ascending:false}).limit(20)

    setEnrollments(enr||[])
    setClasses(cls)
    setPayments(pay||[])
    setCourses(crss)
    setNotifications(notifs||[])
    setLoading(false)
  }, [profile.id])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    if (!AUTO_REFRESH_ENABLED) return
    let debounceTimer = null
    const debouncedLoad = () => {
      clearTimeout(debounceTimer)
      debounceTimer = setTimeout(() => load(), 1500)
    }
    const ch = supabase.channel('student-all-rt')
      .on('postgres_changes',{event:'INSERT',schema:'public',table:'notifications',filter:`user_id=eq.${profile.id}`},
        payload => setNotifications(prev=>[payload.new,...prev]))
      .on('postgres_changes',{event:'*',schema:'public',table:'enrollments',filter:`student_id=eq.${profile.id}`}, debouncedLoad)
      .on('postgres_changes',{event:'*',schema:'public',table:'classes'}, debouncedLoad)
      .on('postgres_changes',{event:'*',schema:'public',table:'payments',filter:`student_id=eq.${profile.id}`}, debouncedLoad)
      .subscribe()
    return () => {
      clearTimeout(debounceTimer)
      supabase.removeChannel(ch)
    }
  }, [profile.id, load])

  async function markNotifRead(id) {
    await supabase.from('notifications').update({is_read:true}).eq('id',id)
    setNotifications(prev=>prev.map(n=>n.id===id?{...n,is_read:true}:n))
  }

  const today    = new Date().toISOString().slice(0,10)
  const upcoming = classes.filter(c=>c.class_date>=today)
  const past     = classes.filter(c=>c.class_date<today)
  const unreadCount = notifications.filter(n=>!n.is_read).length
  const totalDue  = payments.filter(p=>p.status!=='paid').reduce((a,p)=>a+(p.amount||0),0)
  const totalPaid = payments.filter(p=>p.status==='paid').reduce((a,p)=>a+(p.amount||0),0)
  const hasPending = payments.some(p=>['pending','overdue'].includes(p.status))
  const sc = { paid:'#10b981', pending:'#f4a335', overdue:'#f87171' }
  const sb = { paid:'rgba(16,185,129,0.12)', pending:'rgba(232,124,30,0.12)', overdue:'rgba(239,68,68,0.12)' }

  const TABS = [
    { id:'overview',    label: isMobile ? '📋' : '📋 Overview'   },
    { id:'classes',     label: isMobile ? '📹' : '📹 My Classes'  },
    { id:'courses',     label: isMobile ? '🎓' : '🎓 My Courses'  },
    { id:'payments',    label: isMobile ? '💳' : '💳 Payments'    },
    { id:'attendance',  label: isMobile ? '✅' : '✅ Attendance'  },
    { id:'calendar',    label: isMobile ? '📅' : '📅 Calendar'    },
    { id:'resources',   label: isMobile ? '📚' : '📚 Resources'   },
    { id:'recordings',  label: isMobile ? '🎥' : '🎥 Recordings'  },
    { id:'reschedule',  label: isMobile ? '🔄' : '🔄 Reschedule'  },
    { id:'support',     label: isMobile
      ? (unreadCount > 0 ? `🎫(${unreadCount})` : '🎫')
      : (unreadCount > 0 ? `🎫 Support (${unreadCount})` : '🎫 Support') },
  ]

  const tabStyle = k => ({
    padding: isMobile ? '8px 10px' : '8px 16px',
    fontSize: isMobile ? '12px' : '13px',
    fontWeight: 600,
    borderRadius: '10px',
    border: 'none',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.15s',
    background: tab===k ? '#1e90ff' : 'rgba(var(--fg-rgb),0.06)',
    color: tab===k ? '#fff' : 'rgba(var(--fg-rgb),var(--a45))',
    flex: isMobile ? '1' : 'none',
    textAlign: 'center',
    whiteSpace: 'nowrap',
  })

  return (
    <Layout profile={profile} pageTitle="My Learning">
      <PageHeader title={`Hey ${profile.full_name?.split(' ')[0]||'there'} 👋`} subtitle={profile.student_id ? `Student ID: ${profile.student_id}` : 'Welcome to UniEDD'} />

      <div style={{ display:'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: isMobile ? '10px' : '12px', marginBottom:'16px' }}>
        <MetricCard icon="🎓" label="Enrolled" value={courses.length||enrollments.length}/>
        <MetricCard icon="📅" label="Upcoming" value={upcoming.length}/>
        <MetricCard icon="✅" label="Completed" value={past.length}/>
        {hasPending
          ? <div style={{ background:'rgba(232,124,30,0.1)', border:'1px solid rgba(232,124,30,0.3)', borderRadius:'14px', padding:'14px', cursor:'pointer' }} onClick={()=>setTab('payments')}>
              <div style={{ fontSize:'18px', marginBottom:'8px' }}>💳</div>
              <div style={{ fontSize:'10px', fontWeight:700, color:'#f4a335', letterSpacing:'0.1em', textTransform:'uppercase' }}>Fee Due</div>
              <div style={{ fontSize:'22px', fontWeight:800, color:'#f4a335', marginTop:'3px' }}>₹{totalDue.toLocaleString('en-IN')}</div>
            </div>
          : <MetricCard icon="💳" label="Fees Paid" value={`₹${totalPaid.toLocaleString('en-IN')}`}/>
        }
      </div>

      {/* Tab nav — scrollable on mobile */}
      <div style={{ display:'flex', gap:'6px', marginBottom:'16px', overflowX: isMobile ? 'auto' : 'visible', paddingBottom: isMobile ? '4px' : 0, WebkitOverflowScrolling:'touch' }}>
        {TABS.map(t=><button key={t.id} onClick={()=>setTab(t.id)} style={tabStyle(t.id)}>{t.label}</button>)}
      </div>

      {tab==='overview' && (
        <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap:'14px' }}>
          <Panel title="Upcoming Classes">
            {loading ? <Empty msg="Loading..."/> :
             upcoming.length===0 ? <Empty msg="No upcoming classes."/> :
             upcoming.slice(0,5).map(c=>(
              <div key={c.id} style={{ display:'flex', alignItems:'center', gap:'10px', padding:'9px 0', borderBottom:'0.5px solid rgba(var(--fg-rgb),0.05)' }}>
                <div style={{ width:'32px', height:'32px', borderRadius:'8px', background:c.is_emergency?'rgba(239,68,68,0.15)':'rgba(30,144,255,0.15)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'16px', flexShrink:0 }}>
                  {c.is_emergency?'🚨':'📹'}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:'13px', color:'rgba(var(--fg-rgb),var(--a85))', fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{c.title}</div>
                  <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a35))', marginTop:'2px' }}>{c.class_date} {c.start_time&&`· ${c.start_time}`}</div>
                </div>
                {c.meet_link && !isMeetLinkVisible(c.class_date, c.start_time)
                  ? <span style={{ fontSize:'10px', fontStyle:'italic', color:'rgba(var(--fg-rgb),var(--a20))', flexShrink:0 }}>🔒 24h before</span>
                  : <ZoomBtn link={c.meet_link} onClick={() => markJoined(c.id)}/>}
              </div>
             ))}
          </Panel>
          <Panel title={`Notifications${unreadCount>0?` (${unreadCount})`:''}`}>
            {notifications.length===0 ? <Empty msg="No notifications yet."/> :
             notifications.slice(0,6).map(n=>(
              <div key={n.id} onClick={()=>markNotifRead(n.id)}
                style={{ padding:'9px 0', borderBottom:'0.5px solid rgba(var(--fg-rgb),0.05)', cursor:'pointer', opacity:n.is_read?0.5:1 }}>
                <div style={{ fontSize:'12px', color:n.is_read?'rgba(var(--fg-rgb),var(--a40))':'rgba(var(--fg-rgb),var(--a85))', fontWeight:n.is_read?400:600 }}>{n.message||n.title}</div>
                <div style={{ fontSize:'10px', color:'rgba(var(--fg-rgb),var(--a25))', marginTop:'2px' }}>{new Date(n.created_at).toLocaleDateString()}</div>
              </div>
             ))}
          </Panel>
        </div>
      )}

      {tab==='classes' && (
        <>
          <PageHeader title="My Classes" subtitle="All your classes — sorted by date & time."/>
          {loading ? <Empty msg="Loading..."/> : classes.length===0 ? (
            <div style={{ textAlign:'center', padding:'3rem 1rem', background:'rgba(var(--fg-rgb),0.03)', borderRadius:'14px' }}>
              <div style={{ fontSize:'40px', marginBottom:'12px' }}>📚</div>
              <div style={{ fontSize:'14px', color:'rgba(var(--fg-rgb),var(--a40))' }}>No classes assigned yet.</div>
            </div>
          ) : (
            <ClassScheduleView classes={classes} showTeacher role="student" onJoin={cls => markJoined(cls.id)}/>
          )}
        </>
      )}

      {tab==='courses' && (
        <>
          <PageHeader title="My Courses"/>
          {courses.length===0 ? (
            <div style={{ textAlign:'center', padding:'3rem', background:'rgba(var(--fg-rgb),0.03)', borderRadius:'14px' }}>
              <div style={{ fontSize:'40px', marginBottom:'12px' }}>🎓</div>
              <div style={{ fontSize:'14px', color:'rgba(var(--fg-rgb),var(--a40))' }}>No courses enrolled yet.</div>
            </div>
          ) : (
            <div style={{ display:'grid', gridTemplateColumns:`repeat(auto-fill,minmax(${isMobile?'160px':'220px'},1fr))`, gap:'12px' }}>
              {courses.map(c=>(
                <div key={c.id} style={{ background:'rgba(var(--fg-rgb),0.04)', border:'0.5px solid rgba(var(--fg-rgb),0.08)', borderRadius:'14px', padding:'16px' }}>
                  <div style={{ fontSize:'24px', marginBottom:'10px' }}>🎵</div>
                  <div style={{ fontSize:'14px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a85))', marginBottom:'4px' }}>{c.title}</div>
                  {c.level && <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a40))' }}>Level: {c.level}</div>}
                  {c.fee && <div style={{ fontSize:'13px', fontWeight:700, color:'#10b981', marginTop:'8px' }}>₹{c.fee.toLocaleString('en-IN')}/mo</div>}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {tab==='payments' && (
        <>
          <PageHeader title="My Payments"/>
          <Panel>
            {payments.length===0 ? (
              <div style={{ textAlign:'center', padding:'2.5rem' }}>
                <div style={{ fontSize:'32px', marginBottom:'10px' }}>💳</div>
                <div style={{ fontSize:'13px', color:'rgba(var(--fg-rgb),var(--a30))' }}>No payment records yet.</div>
              </div>
            ) : (
              <div style={{ display:'grid', gap:'8px' }}>
                <div style={{ display:'flex', gap:'10px', marginBottom:'6px', flexWrap:'wrap' }}>
                  <div style={{ padding:'6px 14px', borderRadius:'20px', background:'rgba(16,185,129,0.1)', fontSize:'12px', fontWeight:700, color:'#10b981' }}>Paid: ₹{totalPaid.toLocaleString('en-IN')}</div>
                  {totalDue>0 && <div style={{ padding:'6px 14px', borderRadius:'20px', background:'rgba(232,124,30,0.1)', fontSize:'12px', fontWeight:700, color:'#f4a335' }}>Due: ₹{totalDue.toLocaleString('en-IN')}</div>}
                </div>
                {payments.map(p=>(
                  <div key={p.id} style={{ padding:'12px 14px', background:'rgba(var(--fg-rgb),0.03)', borderRadius:'10px', borderLeft:`3px solid ${sc[p.status]||'#888'}` }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:'10px', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:'13px', fontWeight:600, color:'rgba(var(--fg-rgb),var(--a85))' }}>{p.course_name||'Course Fee'}</div>
                        {!isMobile && p.due_date && <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a30))', marginTop:'2px' }}>Due: {p.due_date}</div>}
                      </div>
                      <div style={{ textAlign:'right', flexShrink:0 }}>
                        <div style={{ fontSize:'16px', fontWeight:800, color:sc[p.status]||'var(--fg-solid)' }}>
                          {p.currency==='USD'?'$':p.currency==='GBP'?'£':'₹'}{(p.amount||0).toLocaleString('en-IN')}
                        </div>
                        <span style={{ fontSize:'9px', fontWeight:700, padding:'2px 8px', borderRadius:'10px', background:sb[p.status]||'rgba(var(--fg-rgb),0.05)', color:sc[p.status]||'#aaa', textTransform:'uppercase' }}>{p.status}</span>
                      </div>
                    </div>
                    {p.payment_link && p.status!=='paid' && (
                      <a href={checkoutUrl({ link: p.payment_link, amount: p.amount, currency: p.currency, course: p.course_name, invoiceNo: p.invoice_no, studentName: p.student_name })} target="_blank" rel="noreferrer"
                        style={{ display:'inline-flex', alignItems:'center', gap:'6px', marginTop:'10px', fontSize:'13px', fontWeight:700, padding:'8px 18px', borderRadius:'10px', background:'rgba(24,119,242,0.2)', color:'#5aabff', border:'0.5px solid rgba(24,119,242,0.35)', textDecoration:'none', width: isMobile ? '100%' : 'auto', justifyContent:'center' }}>
                        💳 Pay Now →
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Panel>
        </>
      )}

      {tab==='attendance' && <><PageHeader title="My Attendance"/><Attendance profile={profile}/></>}
      {tab==='support'    && <><PageHeader title="Support"/><SupportChat profile={profile}/></>}

      {/* FIX #6: These components now only mount when their tab is active */}
      {tab==='calendar'   && <><PageHeader title="Calendar"/><Calendar profile={profile}/></>}
      {tab==='resources'  && <><PageHeader title="Resources"/><Resources profile={profile}/></>}
      {tab==='recordings' && <><PageHeader title="Recordings"/><ZoomRecordings profile={profile}/></>}
      {tab==='reschedule' && <><PageHeader title="Reschedule"/><RescheduleManager profile={profile}/></>}
    </Layout>
  )
}
